/**
 * CheckInV2DetailView
 *
 * Read-only detail view for a completed Smart Check-in (V2).
 * Shows enhanced notes prominently, with objectives and provider updates
 * in cards below. Transcript is hidden by default, toggled via header button.
 */
import { format } from 'date-fns';
import {
  ArrowLeft,
  Check,
  CheckCircle2,
  ChevronDown,
  Clock,
  Copy,
  FileText,
  Sparkles,
  XCircle,
} from 'lucide-react';
import { useCallback, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';

import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { ScrollArea } from '@/components/ui/scroll-area';
import { useMatter } from '@/hooks/useMatter';
import { cn } from '@/lib/utils';
import {
  type CheckInTranscriptSegment,
  type TreatmentCheckInV2,
  type TreatmentCheckInV2Status,
  useGetCheckInTranscriptSegmentsQuery,
  useGetCheckInV2Query,
} from '@/services/api/treatmentCheckInV2Api';

// ── Helpers ──────────────────────────────────────────────────────────────────

function statusBadgeConfig(status: TreatmentCheckInV2Status): {
  label: string;
  className: string;
} {
  switch (status) {
    case 'FINALIZED':
      return {
        label: 'Finalized',
        className: 'bg-emerald-100 text-emerald-700',
      };
    case 'IN_REVIEW':
      return {
        label: 'In Review',
        className: 'bg-purple-100 text-purple-700',
      };
    case 'NOTES_TAKEN':
      return { label: 'Notes Taken', className: 'bg-sky-100 text-sky-700' };
    case 'NO_ANSWER':
      return { label: 'No Answer', className: 'bg-gray-100 text-gray-600' };
    case 'LEFT_VOICEMAIL':
      return {
        label: 'Left Voicemail',
        className: 'bg-gray-100 text-gray-600',
      };
    case 'LEFT_TEXT':
      return { label: 'Left Text', className: 'bg-gray-100 text-gray-600' };
    default:
      return { label: 'Draft', className: 'bg-gray-100 text-gray-600' };
  }
}

function callOutcomeLabel(outcome: string | null): string | null {
  switch (outcome) {
    case 'CONNECTED':
      return 'Connected';
    case 'NO_ANSWER':
      return 'No Answer';
    case 'LEFT_VOICEMAIL':
      return 'Left Voicemail';
    case 'LEFT_TEXT':
      return 'Left Text';
    default:
      return null;
  }
}

// ── Transcript Bubble ────────────────────────────────────────────────────────

const TranscriptBubble = ({
  segment,
}: {
  segment: CheckInTranscriptSegment;
}) => {
  const isOperator = segment.speaker === 0 || segment.speaker === null;
  return (
    <div className={cn('flex', isOperator ? 'justify-start' : 'justify-end')}>
      <div
        className={cn(
          'max-w-[85%] rounded-xl px-3 py-2 text-xs font-medium leading-relaxed',
          isOperator
            ? 'rounded-tl-sm bg-slate-800 text-slate-100'
            : 'rounded-tr-sm bg-sky-100 text-slate-900',
        )}
      >
        {segment.text}
      </div>
    </div>
  );
};

const CopyTranscriptButton = ({
  segments,
}: {
  segments: CheckInTranscriptSegment[];
}) => {
  const [copied, setCopied] = useState(false);
  const handleCopy = useCallback(async () => {
    const text = segments
      .map(
        (s) =>
          `${s.speaker === 1 ? 'Provider' : 'Operator'}: ${s.text}`,
      )
      .join('\n');
    await navigator.clipboard.writeText(text);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  }, [segments]);

  return (
    <Button
      variant="ghost"
      size="icon"
      className="h-7 w-7"
      onClick={handleCopy}
    >
      {copied ? (
        <Check className="h-3.5 w-3.5 text-green-600" />
      ) : (
        <Copy className="h-3.5 w-3.5 text-muted-foreground" />
      )}
    </Button>
  );
};

// ── Sections ─────────────────────────────────────────────────────────────────

const EnhancedNotesCard = ({ checkIn }: { checkIn: TreatmentCheckInV2 }) => {
  const [showOriginal, setShowOriginal] = useState(false);
  const extraction = checkIn.extraction_result as Record<string, unknown> | null;
  const rawEnhanced = extraction?.enhanced_notes;
  const enhancedNotes = Array.isArray(rawEnhanced)
    ? (rawEnhanced as Array<{ text: string; source: string }>)
    : undefined;
  const enhancedHtml = typeof rawEnhanced === 'string' ? rawEnhanced : undefined;
  const hasEnhanced = (enhancedNotes && enhancedNotes.length > 0) || !!enhancedHtml;

  return (
    <div className="rounded-lg border bg-white p-5">
      <div className="mb-4 flex items-center gap-2">
        <Sparkles className="h-4 w-4 text-purple-600" />
        <span className="text-sm font-bold text-gray-900">
          {hasEnhanced ? 'Enhanced Notes' : 'Operator Notes'}
        </span>
      </div>
      <div className="space-y-1">
        {enhancedNotes && enhancedNotes.length > 0 ? (
          enhancedNotes.map((segment, idx) => (
            <p
              key={idx}
              className={cn(
                'text-sm font-medium leading-7',
                segment.source === 'original'
                  ? 'text-gray-900'
                  : 'text-gray-500',
              )}
            >
              {segment.text}
            </p>
          ))
        ) : enhancedHtml ? (
          <div
            className="prose prose-sm max-w-none text-gray-800"
            dangerouslySetInnerHTML={{ __html: enhancedHtml }}
          />
        ) : (
          <div
            className="prose prose-sm max-w-none text-gray-800"
            dangerouslySetInnerHTML={{
              __html: checkIn.operator_notes || 'No notes recorded.',
            }}
          />
        )}
      </div>
      {checkIn.operator_notes && hasEnhanced && (
        <button
          type="button"
          className="mt-3 flex items-center gap-1.5 text-xs font-medium text-gray-500"
          onClick={() => setShowOriginal((prev) => !prev)}
        >
          <ChevronDown
            className={cn('h-3 w-3 transition-transform', showOriginal && 'rotate-180')}
          />
          {showOriginal ? 'Hide original notes' : 'View original notes'}
        </button>
      )}
      {showOriginal && (
        <div className="mt-2 rounded-md border bg-gray-50 p-3">
          <div
            className="prose prose-sm max-w-none text-sm text-gray-500"
            dangerouslySetInnerHTML={{ __html: checkIn.operator_notes }}
          />
        </div>
      )}
    </div>
  );
};

const ObjectivesCard = ({ checkIn }: { checkIn: TreatmentCheckInV2 }) => {
  const objectives = (checkIn.objective_state as Record<string, unknown> | null)
    ?.objectives as Array<Record<string, unknown>> | undefined;
  const visibleIds = (checkIn.objective_state as Record<string, unknown> | null)
    ?.visible_objective_ids as string[] | undefined;

  if (!objectives || objectives.length === 0) return null;

  const visible = visibleIds
    ? objectives.filter((o) => visibleIds.includes(o.id as string))
    : objectives.slice(0, 5);

  const assessments = (
    (checkIn.extraction_result as Record<string, unknown> | null)
      ?.objectives_assessment as Array<Record<string, unknown>> | undefined
  ) ?? [];
  const assessmentMap = new Map(
    assessments.map((a) => [a.objective_id as string, a]),
  );

  const coveredCount = visible.filter((o) => {
    const assessment = assessmentMap.get(o.id as string);
    return assessment?.assessment === 'covered';
  }).length;

  return (
    <div className="rounded-lg border bg-white p-4">
      <div className="mb-3 flex items-center gap-2">
        <span className="text-xs font-bold text-gray-900">Objectives</span>
        <Badge variant="default" className="text-[9px]">
          {coveredCount} covered
        </Badge>
        {visible.length - coveredCount > 0 && (
          <Badge variant="secondary" className="text-[9px]">
            {visible.length - coveredCount} remaining
          </Badge>
        )}
      </div>
      <div className="space-y-0.5">
        {visible.map((obj) => {
          const assessment = assessmentMap.get(obj.id as string);
          const isCovered = assessment?.assessment === 'covered';
          const carryForward =
            (
              (checkIn.objective_state as Record<string, unknown> | null)
                ?.carry_forward_suggestions as
                | Array<Record<string, unknown>>
                | undefined
            )?.find(
              (c) =>
                c.objective_id === (obj.id as string) && c.selected === true,
            ) != null;

          return (
            <div
              key={obj.id as string}
              className={cn(
                'flex items-center gap-2 rounded-md px-2 py-2',
                carryForward && 'bg-red-50',
                !carryForward && !isCovered && 'border-b border-gray-100',
              )}
            >
              {isCovered ? (
                <CheckCircle2 className="h-3.5 w-3.5 shrink-0 text-green-600" />
              ) : (
                <XCircle className="h-3.5 w-3.5 shrink-0 text-gray-400" />
              )}
              <span className="flex-1 text-xs font-medium text-gray-900">
                {obj.label as string}
              </span>
              {carryForward && (
                <span className="text-[9px] font-medium text-red-600">
                  → next call
                </span>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
};

const ProviderUpdatesCard = ({
  checkIn,
}: {
  checkIn: TreatmentCheckInV2;
}) => {
  const extraction = checkIn.extraction_result as Record<string, unknown> | null;
  const providerUpdates = (extraction?.provider_updates ?? []) as Array<
    Record<string, unknown>
  >;
  const acceptedChanges = checkIn.accepted_changes?.items ?? {};

  if (providerUpdates.length === 0) return null;

  // Show all updates that aren't explicitly rejected
  const visibleUpdates = providerUpdates.filter((u) => {
    const decision = acceptedChanges[u.review_key as string];
    return !decision || decision.decision !== 'rejected';
  });

  if (visibleUpdates.length === 0) return null;

  return (
    <div className="rounded-lg border bg-white p-4">
      <div className="mb-3 flex items-center gap-2">
        <span className="text-xs font-bold text-gray-900">
          Provider Updates
        </span>
        <Badge variant="secondary" className="text-[9px]">
          {visibleUpdates.length}
        </Badge>
      </div>
      {visibleUpdates.map((update) => (
        <ProviderUpdateRow key={update.review_key as string} update={update} />
      ))}
    </div>
  );
};

/** Extract the display value from a proposed change field.
 *  Fields may be plain strings or objects with { new_value }. */
function extractChangeValue(field: unknown): string | null {
  if (typeof field === 'string') return field;
  if (field && typeof field === 'object' && 'new_value' in field) {
    const val = (field as Record<string, unknown>).new_value;
    return typeof val === 'string' ? val : null;
  }
  return null;
}

const ProviderUpdateRow = ({
  update,
}: {
  update: Record<string, unknown>;
}) => {
  const proposed = (update.proposed_changes ?? {}) as Record<string, unknown>;
  const status = extractChangeValue(proposed.treatment_status);
  const frequency = extractChangeValue(proposed.treatment_frequency);
  const nextAppt = extractChangeValue(proposed.next_appointment_date);
  const notes = extractChangeValue(proposed.treatment_notes);

  return (
    <div className="space-y-1">
      <div className="flex items-center gap-1.5">
        <Check className="h-3 w-3 text-green-600" />
        <span className="text-xs font-semibold text-gray-900">
          {update.provider_name as string}
        </span>
      </div>
      <div className="space-y-0.5 pl-[18px]">
        {status && <DetailRow label="Status" value={status} />}
        {frequency && <DetailRow label="Frequency" value={frequency} />}
        {nextAppt && <DetailRow label="Next Appt" value={nextAppt} />}
        {notes && <DetailRow label="Notes" value={notes} />}
      </div>
    </div>
  );
};

const DetailRow = ({ label, value }: { label: string; value: string }) => (
  <div className="flex gap-1.5">
    <span className="w-16 shrink-0 text-[10px] font-medium text-gray-500">
      {label}
    </span>
    <span className="text-[10px] font-medium text-gray-700">{value}</span>
  </div>
);

// ── Transcript Card ──────────────────────────────────────────────────────────

const TranscriptCard = ({
  segments,
}: {
  segments: CheckInTranscriptSegment[];
}) => (
  <div className="flex flex-col rounded-lg border bg-white">
    <div className="flex items-center justify-between border-b px-4 py-3">
      <div className="flex items-center gap-2">
        <FileText className="h-3.5 w-3.5 text-gray-500" />
        <span className="text-xs font-semibold text-gray-900">Transcript</span>
        <Badge variant="secondary" className="text-[9px]">
          {segments.length} segments
        </Badge>
      </div>
      {segments.length > 0 && <CopyTranscriptButton segments={segments} />}
    </div>
    <ScrollArea className="max-h-[500px] px-4 py-3">
      {segments.length === 0 ? (
        <p className="py-6 text-center text-sm font-medium text-muted-foreground">
          No transcript available
        </p>
      ) : (
        <div className="space-y-1.5">
          {segments.map((segment) => (
            <TranscriptBubble key={segment.id} segment={segment} />
          ))}
        </div>
      )}
    </ScrollArea>
  </div>
);

// ── Main Component ───────────────────────────────────────────────────────────

export const CheckInV2DetailView = () => {
  const { matterId } = useMatter();
  const { checkInId } = useParams<{ checkInId: string }>();
  const navigate = useNavigate();
  const [showTranscript, setShowTranscript] = useState(false);

  const { data: checkIn, isLoading } = useGetCheckInV2Query(
    { matterId, checkInId: checkInId ?? '' },
    { skip: !checkInId },
  );

  const { data: transcriptSegments = [] } =
    useGetCheckInTranscriptSegmentsQuery(
      { matterId, checkInId: checkInId ?? '' },
      { skip: !checkInId },
    );

  if (isLoading || !checkIn) {
    return (
      <div className="flex items-center justify-center py-20">
        <Clock className="mr-2 h-5 w-5 animate-spin text-gray-400" />
        <span className="text-sm font-medium text-gray-500">Loading...</span>
      </div>
    );
  }

  const statusConfig = statusBadgeConfig(checkIn.status);
  const outcomeLabel = callOutcomeLabel(checkIn.call_outcome);
  const dateStr = checkIn.created_at
    ? format(new Date(checkIn.created_at), 'PPp')
    : 'Unknown date';
  const hasTranscript = transcriptSegments.length > 0;

  return (
    <div className="flex h-full flex-col overflow-hidden bg-gray-50">
      {/* Header */}
      <header className="shrink-0 border-b bg-white px-12 pb-4 pt-5">
        <Button
          variant="ghost"
          size="sm"
          onClick={() =>
            navigate(`/matters/${matterId}/treatment/check-ins`)
          }
          className="mb-2 -ml-2 text-gray-500"
        >
          <ArrowLeft className="mr-1 h-4 w-4" />
          Back to Check-ins
        </Button>
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <Sparkles className="h-4 w-4 text-purple-600" />
            <h1 className="text-lg font-bold text-gray-900">
              Smart Check-in
            </h1>
            <span
              className={cn(
                'inline-flex items-center rounded-full px-2.5 py-0.5 text-[10px] font-semibold',
                statusConfig.className,
              )}
            >
              {statusConfig.label}
            </span>
          </div>
          <div className="flex items-center gap-2">
            {hasTranscript && (
              <Button
                variant={showTranscript ? 'default' : 'outline'}
                size="sm"
                onClick={() => setShowTranscript((prev) => !prev)}
                className="text-xs"
              >
                <FileText className="mr-1.5 h-3.5 w-3.5" />
                {showTranscript ? 'Hide Transcript' : 'Show Transcript'}
              </Button>
            )}
          </div>
        </div>
        <div className="mt-1 flex items-center gap-1.5 text-xs font-medium text-gray-500">
          <span>{dateStr}</span>
          {outcomeLabel && (
            <>
              <span className="text-gray-500">·</span>
              <span>{outcomeLabel}</span>
            </>
          )}
        </div>
      </header>

      {/* Body */}
      <div className="flex flex-1 overflow-y-auto p-6 px-12">
        {showTranscript && (
          <div className="mr-4 w-[440px] shrink-0">
            <TranscriptCard segments={transcriptSegments} />
          </div>
        )}

        <div className="flex min-w-0 flex-1 flex-col gap-4">
          {/* Enhanced Notes */}
          <EnhancedNotesCard checkIn={checkIn} />

          {/* Objectives + Provider Updates side by side */}
          <div className="grid grid-cols-1 gap-4 lg:grid-cols-2">
            <ObjectivesCard checkIn={checkIn} />
            <ProviderUpdatesCard checkIn={checkIn} />
          </div>
        </div>
      </div>
    </div>
  );
};
